import React, { useState, useEffect } from 'react';
import { collectionsAPI, reportsAPI } from '../services/api';
import './ManagementPages.css';

const CollectionHistoryPage = () => {
  const [collections, setCollections] = useState([]);
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Filters
  const [selectedAgent, setSelectedAgent] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [page, setPage] = useState(1);
  
  const limit = 100;

  // Fetch agents on mount
  useEffect(() => {
    fetchAgents();
  }, []);

  // Fetch collections when filters change
  useEffect(() => {
    fetchCollections();
  }, [selectedAgent, selectedStatus, page]);

  const fetchAgents = async () => {
    try {
      const data = await reportsAPI.getAgentCollectionSummary();
      const list = (data && (data.data || data)) || [];
      setAgents(list);
    } catch (err) {
      console.error('Error fetching agents:', err);
    }
  };

  const fetchCollections = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const params = {
        page,
        limit,
        ...(selectedAgent && { agentId: selectedAgent }),
        ...(selectedStatus && { status: selectedStatus })
      };
      
      const data = await collectionsAPI.getAllCollections(params);
      const received = data?.data || data || [];
      // If backend returns empty, fallback to mock collections for local testing
      setCollections(received);
    } catch (err) {
      setError(err.message || 'Failed to load collection history');
      // fallback to mock data on error
      setCollections([]);
    } finally {
      setLoading(false);
    }
  };

  // Filter collections by date range (client-side for better performance)
  const filteredCollections = collections.filter(c => {
    if (!startDate && !endDate) return true;
    
    const collectionDate = new Date(c.collection_date);
    
    if (startDate) {
      const start = new Date(startDate);
      start.setHours(0, 0, 0, 0);
      if (collectionDate < start) return false;
    }
    
    if (endDate) {
      const end = new Date(endDate);
      end.setHours(23, 59, 59, 999);
      if (collectionDate > end) return false;
    }
    
    return true;
  });

  // Calculate statistics from filtered data
  const stats = {
    totalCollections: filteredCollections.length,
    totalAmount: filteredCollections.reduce((sum, c) => sum + (c.emi_amount || 0), 0),
    paidCollections: filteredCollections.filter(c => c.status === 'PAID').length,
    paidAmount: filteredCollections
      .filter(c => c.status === 'PAID')
      .reduce((sum, c) => sum + (c.emi_amount || 0), 0),
    pendingCollections: filteredCollections.filter(c => c.status === 'PENDING').length,
    pendingAmount: filteredCollections
      .filter(c => c.status === 'PENDING')
      .reduce((sum, c) => sum + (c.emi_amount || 0), 0),
  };

  const handleClearFilters = () => {
    setSelectedAgent('');
    setSelectedStatus('');
    setStartDate('');
    setEndDate('');
    setPage(1);
  };

  const exportToCSV = () => {
    const headers = ['Date', 'Loan ID', 'Agent', 'Retailer', 'Amount', 'Payment Method', 'Reference', 'Status'];
    const rows = filteredCollections.map(c => [
      new Date(c.collection_date).toLocaleDateString(),
      c.loan_id,
      `${c.agent_first_name} ${c.agent_last_name}`,
      c.retailer_name || 'N/A',
      c.emi_amount,
      c.payment_method,
      c.reference_number || '-',
      c.status
    ]);

    const csv = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `collection_history_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  return (
    <div className="page-container">
      <div className="page-header">
        <h1>Collection History & Reconciliation</h1>
        <p>Complete collection register - Track all collections by date, agent, and status</p>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-4" style={{ marginBottom: '30px' }}>
        <div className="dashboard-card">
          <div className="card-icon">📊</div>
          <div className="card-content">
            <div className="card-label">Total Collections</div>
            <div className="card-value">{stats.totalCollections}</div>
          </div>
        </div>
        <div className="dashboard-card">
          <div className="card-icon">💰</div>
          <div className="card-content">
            <div className="card-label">Total Amount</div>
            <div className="card-value">₹{(stats.totalAmount / 100000).toFixed(2)}L</div>
          </div>
        </div>
        <div className="dashboard-card">
          <div className="card-icon">✅</div>
          <div className="card-content">
            <div className="card-label">Paid (Verified)</div>
            <div className="card-value" style={{ color: '#27ae60' }}>{stats.paidCollections}</div>
          </div>
        </div>
        <div className="dashboard-card">
          <div className="card-icon">⏳</div>
          <div className="card-content">
            <div className="card-label">Pending</div>
            <div className="card-value" style={{ color: '#e74c3c' }}>{stats.pendingCollections}</div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="filters" style={{ marginBottom: '20px', flexWrap: 'wrap', gap: '10px' }}>
        <select
          value={selectedAgent}
          onChange={(e) => {
            setSelectedAgent(e.target.value);
            setPage(1);
          }}
          style={{
            padding: '8px 12px',
            borderRadius: '6px',
            border: '1px solid #ddd',
            fontSize: '14px',
          }}
        >
          <option value="">All Agents</option>
          {agents.map((agent) => (
            <option key={agent.id} value={agent.id}>
              {agent.first_name} {agent.last_name}
            </option>
          ))}
        </select>

        <select
          value={selectedStatus}
          onChange={(e) => {
            setSelectedStatus(e.target.value);
            setPage(1);
          }}
          style={{
            padding: '8px 12px',
            borderRadius: '6px',
            border: '1px solid #ddd',
            fontSize: '14px',
          }}
        >
          <option value="">All Status</option>
          <option value="PAID">Paid (Verified)</option>
          <option value="PENDING">Pending</option>
        </select>

        <input
          type="date"
          value={startDate}
          onChange={(e) => {
            setStartDate(e.target.value);
            setPage(1);
          }}
          style={{
            padding: '8px 12px',
            borderRadius: '6px',
            border: '1px solid #ddd',
            fontSize: '14px',
          }}
          placeholder="From Date"
        />

        <input
          type="date"
          value={endDate}
          onChange={(e) => {
            setEndDate(e.target.value);
            setPage(1);
          }}
          style={{
            padding: '8px 12px',
            borderRadius: '6px',
            border: '1px solid #ddd',
            fontSize: '14px',
          }}
          placeholder="To Date"
        />

        <button
          className="button button-secondary"
          onClick={handleClearFilters}
        >
          Clear Filters
        </button>

        <button
          className="button button-primary"
          onClick={exportToCSV}
        >
          📥 Export CSV
        </button>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      {loading ? (
        <div className="loader"></div>
      ) : (
        <div>
          {/* Master Collection Register */}
          <div className="table-wrapper" style={{ overflowX: 'auto' }}>
            <table className="table" style={{ fontSize: '13px' }}>
              <thead style={{ background: '#f8f9fa', stickyTop: 0 }}>
                <tr>
                  <th style={{ minWidth: '100px' }}>Date</th>
                  <th style={{ minWidth: '100px' }}>Loan ID</th>
                  <th style={{ minWidth: '150px' }}>Agent Name</th>
                  <th style={{ minWidth: '150px' }}>Retailer</th>
                  <th style={{ minWidth: '100px' }}>EMI Amount</th>
                  <th style={{ minWidth: '120px' }}>Payment Method</th>
                  <th style={{ minWidth: '110px' }}>Reference #</th>
                  <th style={{ minWidth: '90px' }}>Status</th>
                </tr>
              </thead>
              <tbody>
                {filteredCollections.length > 0 ? (
                  filteredCollections.map((collection, idx) => (
                    <tr key={collection.id || idx} style={{ borderBottom: '1px solid #e0e0e0' }}>
                      <td style={{ fontWeight: '500' }}>
                        {new Date(collection.collection_date).toLocaleDateString('en-IN')}
                      </td>
                      <td>
                        <code style={{ background: '#f0f0f0', padding: '2px 6px', borderRadius: '3px', fontSize: '12px' }}>
                          {collection.loan_id}
                        </code>
                      </td>
                      <td>{collection.agent_first_name} {collection.agent_last_name}</td>
                      <td>{collection.retailer_name || 'N/A'}</td>
                      <td style={{ fontWeight: '600', color: '#2c3e50' }}>
                        ₹{(collection.emi_amount || 0).toLocaleString('en-IN')}
                      </td>
                      <td>{collection.payment_method}</td>
                      <td style={{ color: '#7f8c8d' }}>{collection.reference_number || '-'}</td>
                      <td>
                        <span
                          style={{
                            background: collection.status === 'PAID' ? '#d4edda' : '#fff3cd',
                            color: collection.status === 'PAID' ? '#155724' : '#856404',
                            padding: '4px 8px',
                            borderRadius: '4px',
                            fontSize: '12px',
                            fontWeight: 'bold'
                          }}
                        >
                          {collection.status}
                        </span>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="8" style={{ textAlign: 'center', padding: '30px', color: '#999' }}>
                      No collections found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Summary Footer */}
          {filteredCollections.length > 0 && (
            <div style={{
              marginTop: '20px',
              padding: '15px',
              background: '#f8f9fa',
              borderRadius: '6px',
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
              gap: '15px'
            }}>
              <div>
                <div style={{ fontSize: '12px', color: '#666', marginBottom: '5px' }}>Total Verified</div>
                <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#27ae60' }}>
                  ₹{(stats.paidAmount / 100000).toFixed(2)}L ({stats.paidCollections})
                </div>
              </div>
              <div>
                <div style={{ fontSize: '12px', color: '#666', marginBottom: '5px' }}>Pending Verification</div>
                <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#e74c3c' }}>
                  ₹{(stats.pendingAmount / 100000).toFixed(2)}L ({stats.pendingCollections})
                </div>
              </div>
              <div>
                <div style={{ fontSize: '12px', color: '#666', marginBottom: '5px' }}>Grand Total</div>
                <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#3498db' }}>
                  ₹{(stats.totalAmount / 100000).toFixed(2)}L
                </div>
              </div>
            </div>
          )}

          {/* Pagination */}
          {filteredCollections.length > 0 && (
            <div style={{ marginTop: '20px', display: 'flex', justifyContent: 'center', gap: '10px' }}>
              <button
                className="button button-secondary"
                onClick={() => setPage(Math.max(1, page - 1))}
                disabled={page === 1}
              >
                ← Previous
              </button>
              <span style={{ padding: '8px 16px', background: '#f0f0f0', borderRadius: '4px', fontWeight: '500' }}>
                Page {page}
              </span>
              <button
                className="button button-secondary"
                onClick={() => setPage(page + 1)}
                disabled={filteredCollections.length < limit}
              >
                Next →
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CollectionHistoryPage;
